import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Coins, Factory, Layers3 } from 'lucide-react'
import { creditsApi, type UsageByServiceRow, type UsageDaily, type UsageServiceKind } from '@/api/creditsApi'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SERVICE_COLORS, SERVICE_KINDS } from '@/lib/serviceColors'
import { formatCount, formatCredits } from '@/utils/format'

interface UsageOverviewProps {
  userId?: string
}

export function isLocalUsageModel(model: { model_alias: string; source?: string }): boolean {
  return model.model_alias.startsWith('openai/local/') || model.source === 'local'
}

function serviceLabelKey(kind: UsageServiceKind): string {
  return `billing.usage.services.${kind}`
}

function TokenSplitDetails({ row, t }: { row: UsageByServiceRow; t: (key: string) => string }) {
  if (row.unit_type !== 'token') return null
  return (
    <div className="ml-[124px] flex flex-wrap gap-x-2 text-xs text-muted-foreground">
      <span className="nf-mono">
        {t('billing.usage.inputTokens')} {formatCount(row.input_tokens ?? 0)}
        {' / '}
        {t('billing.usage.outputTokens')} {formatCount(row.output_tokens ?? 0)}
        {' / '}
        {t('billing.usage.cachedInputTokens')} {formatCount(row.cached_input_tokens ?? 0)}
      </span>
      {row.has_missing_token_split ? <span>{t('billing.usage.partialTokenSplit')}</span> : null}
    </div>
  )
}

export default function UsageOverview({ userId }: UsageOverviewProps) {
  const { t } = useTranslation()
  const now = new Date()
  const currentMonth = now.toISOString().slice(0, 7)
  const [month, setMonth] = useState(currentMonth)

  const months = useMemo(() => {
    const result: string[] = []
    const [year, mon] = currentMonth.split('-').map(Number)
    const startDate = new Date(Date.UTC(year, mon - 1, 1))
    for (let i = 0; i < 12; i++) {
      const d = new Date(startDate)
      d.setUTCMonth(d.getUTCMonth() - i)
      result.push(d.toISOString().slice(0, 7))
    }
    return result
  }, [currentMonth])

  const dailyQuery = useQuery<UsageDaily>({
    queryKey: ['billing', 'usage', 'daily', userId, month],
    queryFn: () => creditsApi.usageDaily(month, userId),
    enabled: !!userId,
  })

  const serviceQuery = useQuery({
    queryKey: ['billing', 'usage', 'by-service', userId, month],
    queryFn: () => creditsApi.usageByService(month, userId),
    enabled: !!userId,
  })

  const monthSeries = useMemo(() => {
    const [year, mon] = month.split('-').map(Number)
    const daysInMonth = new Date(Date.UTC(year, mon, 0)).getUTCDate()
    const creditsByDate = new Map((dailyQuery.data?.daily ?? []).map((d) => [d.date, d.credits]))
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1
      const date = `${month}-${String(day).padStart(2, '0')}`
      return { date, day, credits: creditsByDate.get(date) ?? 0 }
    })
  }, [month, dailyQuery.data])

  const maxDailyCredits = useMemo(
    () => monthSeries.reduce((mx, s) => Math.max(mx, s.credits), 0),
    [monthSeries],
  )

  const linePoints = useMemo(() => {
    const n = monthSeries.length
    if (!n) return ''
    return monthSeries
      .map((item, i) => {
        const h = item.credits > 0 && maxDailyCredits > 0 ? Math.max((item.credits / maxDailyCredits) * 100, 2) : 0
        const x = ((i + 0.5) / n) * 100
        const y = 100 - h
        return `${x.toFixed(2)},${y.toFixed(2)}`
      })
      .join(' ')
  }, [monthSeries, maxDailyCredits])

  const serviceRows = useMemo(() => {
    const rows = serviceQuery.data?.rows ?? []
    return SERVICE_KINDS.map((kind) => {
      const row = rows.find((item) => item.service_kind === kind)
      return {
        service_kind: kind,
        unit_type: row?.unit_type ?? '',
        credits: row?.credits ?? 0,
        quantity: row?.quantity ?? 0,
        input_tokens: row?.input_tokens ?? 0,
        output_tokens: row?.output_tokens ?? 0,
        cached_input_tokens: row?.cached_input_tokens ?? 0,
        has_missing_token_split: row?.has_missing_token_split ?? false,
      }
    })
  }, [serviceQuery.data])

  const totalServiceCredits = useMemo(
    () => serviceRows.reduce((sum, row) => sum + row.credits, 0),
    [serviceRows],
  )
  const maxServiceCredits = useMemo(
    () => serviceRows.reduce((mx, row) => Math.max(mx, row.credits), 0),
    [serviceRows],
  )
  const isLoading = dailyQuery.isLoading || serviceQuery.isLoading
  const modelCredits = dailyQuery.data?.by_model?.reduce((sum, model) => sum + model.credits, 0) ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-normal">{t('billing.usage.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('billing.usage.description')}</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('billing.usage.month')} />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium">{t('billing.usage.monthCredits')}</CardTitle>
              <CardDescription>{t('billing.usage.monthCreditsSubtitle')}</CardDescription>
            </div>
            <Coins className="size-4 text-primary" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="nf-mono text-2xl font-bold">{formatCredits(dailyQuery.data?.month_spend_credits ?? totalServiceCredits)}</div>}
          </CardContent>
        </Card>
        <Card className="rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium">{t('billing.usage.cumulativeCredits')}</CardTitle>
              <CardDescription>{t('billing.usage.cumulativeCreditsSubtitle')}</CardDescription>
            </div>
            <Factory className="size-4 text-primary" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="nf-mono text-2xl font-bold">{formatCredits(dailyQuery.data?.cumulative_credits)}</div>}
          </CardContent>
        </Card>
        <Card className="rounded-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('billing.usage.modelCredits')}</CardTitle>
            <Layers3 className="size-4 text-primary" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : <div className="nf-mono text-2xl font-bold">{formatCredits(modelCredits)}</div>}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle>{t('billing.usage.byServiceTitle')}</CardTitle>
          <CardDescription>{t('billing.usage.byServiceDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {serviceQuery.isLoading ? (
            <Skeleton className="h-44 w-full" />
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <div className="space-y-3">
                {serviceRows.map((row) => {
                  const width = row.credits > 0 && maxServiceCredits > 0 ? Math.max((row.credits / maxServiceCredits) * 100, 3) : 0
                  return (
                    <div key={row.service_kind} className="space-y-1">
                      <div className="grid grid-cols-[112px_minmax(0,1fr)_96px] items-center gap-3 text-sm">
                        <span className="flex items-center gap-1.5 truncate text-muted-foreground">
                          <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: SERVICE_COLORS[row.service_kind] }} />
                          {t(serviceLabelKey(row.service_kind))}
                        </span>
                        <div className="h-6 overflow-hidden rounded-md bg-muted">
                          <div className="h-full rounded-md" style={{ width: `${width}%`, backgroundColor: SERVICE_COLORS[row.service_kind] }} />
                        </div>
                        <span className="nf-mono text-right">{formatCredits(row.credits)}</span>
                      </div>
                      <TokenSplitDetails row={row} t={t} />
                    </div>
                  )
                })}
              </div>
              <svg className="h-44 w-full text-primary" viewBox="0 0 120 120" role="img" aria-label={t('billing.usage.serviceShareChart')}>
                <circle cx="60" cy="60" r="42" fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="18" />
                {serviceRows.map((row, index) => {
                  const share = totalServiceCredits > 0 ? row.credits / totalServiceCredits : 0
                  const circumference = 2 * Math.PI * 42
                  const previous = serviceRows.slice(0, index).reduce((sum, item) => sum + (totalServiceCredits > 0 ? item.credits / totalServiceCredits : 0), 0)
                  return (
                    <circle
                      key={row.service_kind}
                      cx="60"
                      cy="60"
                      r="42"
                      fill="none"
                      stroke={SERVICE_COLORS[row.service_kind]}
                      strokeWidth="18"
                      strokeDasharray={`${share * circumference} ${circumference}`}
                      strokeDashoffset={-(previous * circumference)}
                      transform="rotate(-90 60 60)"
                    />
                  )
                })}
                <text x="60" y="56" textAnchor="middle" className="fill-current text-[13px] font-semibold">
                  {formatCredits(totalServiceCredits)}
                </text>
                <text x="60" y="74" textAnchor="middle" className="fill-muted-foreground text-[9px]">
                  {t('billing.usage.creditsUnit')}
                </text>
              </svg>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle>{t('billing.usage.dailyTitle')}</CardTitle>
          <CardDescription>{t('billing.usage.dailyDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {dailyQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="flex h-40 flex-col">
              <div className="relative flex-1">
                <div className="absolute inset-0 flex items-end gap-[3px] px-2">
                  {monthSeries.map((item) => {
                    const height = item.credits > 0 && maxDailyCredits > 0 ? Math.max((item.credits / maxDailyCredits) * 100, 2) : 0
                    return (
                      <div key={item.date} className="flex h-full min-w-0 flex-1 items-end">
                        <div
                          className="w-full rounded-t bg-primary/55 transition-colors hover:bg-primary/90"
                          style={{ height: `${height}%` }}
                          title={`${item.date}: ${formatCredits(item.credits)} ${t('billing.usage.creditsUnit')}`}
                        />
                      </div>
                    )
                  })}
                </div>
                <svg className="pointer-events-none absolute inset-0 h-full w-full text-sky-500 dark:text-sky-400" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                  <polyline points={linePoints} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </svg>
              </div>
              <div className="mt-1 flex gap-[3px] px-2">
                {monthSeries.map((item) => (
                  <span key={item.date} className="h-4 min-w-0 flex-1 text-center text-[9px] leading-4 tabular-nums text-muted-foreground">
                    {item.day}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardHeader>
          <CardTitle>{t('billing.usage.byModelTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {dailyQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : dailyQuery.data?.by_model && dailyQuery.data.by_model.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('billing.usage.colModel')}</TableHead>
                  <TableHead>{t('billing.usage.colSource')}</TableHead>
                  <TableHead className="text-right">{t('billing.usage.colTokens')}</TableHead>
                  <TableHead className="text-right">{t('billing.usage.colCredits')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dailyQuery.data.by_model.map((model, idx) => (
                  <TableRow key={`${model.model_alias}-${idx}`}>
                    <TableCell className="font-medium">{model.model_alias}</TableCell>
                    <TableCell>
                      <Badge variant={isLocalUsageModel(model) ? 'secondary' : 'default'}>
                        {isLocalUsageModel(model) ? t('billing.usage.sourceLocal') : t('billing.usage.sourceCloud')}
                      </Badge>
                    </TableCell>
                    <TableCell className="nf-mono text-right">{formatCount(model.tokens)}</TableCell>
                    <TableCell className="nf-mono text-right">{formatCredits(model.credits)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex h-40 items-center justify-center text-muted-foreground">{t('billing.usage.empty')}</div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
